"use client";

import { useCallback, useRef } from "react";
import { useEffect } from "react";

export type ComboboxMultiFieldFilterOptions<Item> = {
  alphaNumericSelectors?: Array<(item: Item) => string>;
  digitSelectors?: Array<(item: Item) => string>;
  textSelectors?: Array<(item: Item) => string>;

  alphaNumericMatchMode?: "includes" | "startsWith";
  digitMatchMode?: "includes" | "startsWith";
  textMatchMode?: "includes" | "startsWith";

  emptyQueryReturnsAll?: boolean;
};

function matchString(
  source: string,
  query: string,
  mode: "includes" | "startsWith",
) {
  if (mode === "startsWith") return source.startsWith(query);
  return source.includes(query);
}

/**
 * Builds a `filter` function compatible with Base UI Combobox.
 */
export function useComboboxMultiFieldFilter<Item>({
  textSelectors = [],
  alphaNumericSelectors = [],
  digitSelectors = [],
  textMatchMode = "includes",
  alphaNumericMatchMode = "includes",
  digitMatchMode = "includes",
  emptyQueryReturnsAll = true,
}: ComboboxMultiFieldFilterOptions<Item>) {
  const optionsRef = useRef({
    textSelectors,
    alphaNumericSelectors,
    digitSelectors,
    textMatchMode,
    alphaNumericMatchMode,
    digitMatchMode,
    emptyQueryReturnsAll,
  });

  // Keep the latest selectors/options without changing the filter function reference.
  useEffect(() => {
    optionsRef.current = {
      textSelectors,
      alphaNumericSelectors,
      digitSelectors,
      textMatchMode,
      alphaNumericMatchMode,
      digitMatchMode,
      emptyQueryReturnsAll,
    };
  }, [
    digitMatchMode,
    digitSelectors,
    emptyQueryReturnsAll,
    alphaNumericSelectors,
    textMatchMode,
    alphaNumericMatchMode,
    textSelectors,
  ]);

  return useCallback((item: Item, query: string) => {
    const {
      textSelectors: refTextSelectors,
      alphaNumericSelectors: refAlphaNumericSelectors,
      digitSelectors: refDigitSelectors,
      textMatchMode: refTextMatchMode,
      alphaNumericMatchMode: refAlphaNumericMatchMode,
      digitMatchMode: refDigitMatchMode,
      emptyQueryReturnsAll: refEmptyQueryReturnsAll,
    } = optionsRef.current;

    const trimmed = query.trim();
    if (!trimmed) return refEmptyQueryReturnsAll;

    const hasLetters = /[a-zA-ZÀ-ÖØ-öø-ÿ]/.test(trimmed);
    const hasDigits = /\d/.test(trimmed);

    const qLower = trimmed.toLowerCase();
    const lettersQueryLower = trimmed.replace(/\d/g, "").trim().toLowerCase();
    const digitsQuery = trimmed.replace(/\D/g, "");

    const matchesTextFromLettersOnly =
      refTextSelectors.length > 0 &&
      lettersQueryLower.length > 0 &&
      refTextSelectors.some((sel) =>
        matchString(
          sel(item).toLowerCase(),
          lettersQueryLower,
          refTextMatchMode,
        ),
      );

    const matchesTextFullQuery =
      refTextSelectors.length > 0 &&
      qLower.length > 0 &&
      refTextSelectors.some((sel) =>
        matchString(sel(item).toLowerCase(), qLower, refTextMatchMode),
      );

    const matchesAlphaNumeric =
      refAlphaNumericSelectors.length > 0 &&
      qLower.length > 0 &&
      refAlphaNumericSelectors.some((sel) =>
        matchString(sel(item).toLowerCase(), qLower, refAlphaNumericMatchMode),
      );

    const matchesDigits =
      refDigitSelectors.length > 0 &&
      digitsQuery.length > 0 &&
      refDigitSelectors.some((sel) => {
        const digitsSource = sel(item).replace(/\D/g, "");
        return matchString(digitsSource, digitsQuery, refDigitMatchMode);
      });

    const matchesTextForQuery =
      // In mixed queries, alphaNumericSelectors can help the "letters side" match.
      matchesTextFullQuery || matchesTextFromLettersOnly || matchesAlphaNumeric;

    // Auto behavior:
    // - letters only  => textSelectors
    // - digits only   => digitSelectors
    // - letters+digits => Option A (AND): text AND digits
    if (hasLetters && hasDigits) return matchesTextForQuery && matchesDigits;
    if (hasLetters) return matchesTextForQuery;
    if (hasDigits) return matchesDigits;

    return matchesTextForQuery;
  }, []);
}
